import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { Box, SxProps } from "@mui/material";
import { CircularLoader } from "../loading/CircularLoader";
import { makeStyles } from "@mui/styles";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { polyfillCSS } from "../../utils/css";

enum PullState {
  Idle = "idle",
  Swiping = "swiping", //user started swiping the screen at the top
  Pulling = "pulling", //user pulled down the screen from the top
  ThresholdReached = "threshold_reached",
}

type PullAreaStyleProps = {
  pullState: PullState;
  refreshing: boolean;
  pullDistance: number;
};

const useStyles = makeStyles((theme) => ({
  // A container for the pull indicator
  pullHeader: {
    top: polyfillCSS("var(--safe-area-inset-top)"),
    left: 0,
    right: 0,
    height: "100%", // has to take up the whole height so that the pull indicator won't clip when pulled
    position: "absolute",
    display: "flex",
    alignItems: "top",
    justifyContent: "center",
    overflow: "hidden",
    pointerEvents: "none",
    zIndex: 999999999,
  },

  pullLoader: {
    "& .MuiCircularProgress-circle": {
      strokeLinecap: "round",
      transition: "none !important",
    },
  },

  loaderBox: ({ pullState, refreshing, pullDistance }: PullAreaStyleProps) => ({
    // lower the loader by half of the pull distance to create a pulling effect
    transform: `translateY(calc(${pullState !== PullState.Idle ? pullDistance / 2 : refreshing ? "100%" : 0}px - 100%))`,
    transition:
      pullState === PullState.Idle || refreshing
        ? "transform 0.3s cubic-bezier(0.4,0,0.2,1)"
        : "none",
    background: theme.palette.background.default,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(1.5, 1.5, 1, 1.5),
    border: `2px solid ${theme.palette.divider}`,
    width: "min-content",
    height: "min-content",
  }),
}));

interface PullRefreshProps {
  onRefresh: () => Promise<void>;
  refreshThreshold?: number;
  pullSpeedFactorAfterTreshold?: number;
  loaderSize?: number;
  loaderColor?: string;
  sx?: SxProps;
}

export const PullRefresh: React.FC<PullRefreshProps> = ({
  onRefresh,
  refreshThreshold = 300,
  pullSpeedFactorAfterTreshold = 0.3,
  loaderSize = 36,
  loaderColor = "primary",
}) => {
  const [pullState, setPullState] = useState<PullState>(PullState.Idle);
  const [refreshing, setRefreshing] = useState(false);
  const [loaderPopupDistance, setLoaderPopupDistance] = useState(0);
  const startY = useRef<number | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const styleProps = useMemo(
    () => ({ pullState, refreshing, pullDistance: loaderPopupDistance }),
    [pullState, refreshing, loaderPopupDistance],
  );
  const s = useStyles(styleProps);

  const canPull = (e: TouchEvent) => {
    const targets = document.elementsFromPoint(
      e.targetTouches[0].screenX,
      e.targetTouches[0].screenY,
    );
    return targets.every((el) => el.scrollTop <= 0) && window.scrollY <= 0;
  };

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      setRefreshing(false);
      if (!canPull(e) || refreshing) return;
      startY.current = e.touches[0].clientY;
      setPullState(PullState.Swiping);
    },
    [refreshing],
  );

  const calcLoaderPopupDistance = useCallback(
    (touchDistance: number) => {
      const maxPull = refreshThreshold * 1.5;
      if (touchDistance < 0) return 0;
      let damped;
      if (touchDistance <= refreshThreshold) {
        damped = touchDistance;
      } else {
        damped =
          refreshThreshold +
          (touchDistance - refreshThreshold) * pullSpeedFactorAfterTreshold;
      }
      return Math.min(damped, maxPull);
    },
    [refreshThreshold, pullSpeedFactorAfterTreshold],
  );

  useEffect(() => {
    if (pullState === PullState.ThresholdReached) {
      Haptics.impact({ style: ImpactStyle.Heavy });
    }
  }, [pullState]);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (pullState === PullState.Idle || refreshing || startY.current === null)
        return;
      const dampedDistance = calcLoaderPopupDistance(
        e.touches[0].clientY - startY.current,
      );
      if (dampedDistance > 0) {
        e.preventDefault();
        setPullState(PullState.Pulling);
      }
      setLoaderPopupDistance(dampedDistance);
      if (dampedDistance >= refreshThreshold) {
        setPullState(PullState.ThresholdReached);
      } else if (dampedDistance > 0) {
        setPullState(PullState.Pulling);
      }
    },
    [pullState, refreshing, startY, calcLoaderPopupDistance, refreshThreshold],
  );

  const handleTouchEnd = useCallback(() => {
    if (pullState === PullState.Idle || refreshing) return;
    if (pullState === PullState.ThresholdReached) {
      setRefreshing(true);
      setLoaderPopupDistance(refreshThreshold);
      onRefresh().finally(() => {
        setRefreshing(false);
        setLoaderPopupDistance(0);
        setPullState(PullState.Idle);
      });
    } else {
      setLoaderPopupDistance(0);
      setPullState(PullState.Idle);
    }
  }, [pullState, refreshing, onRefresh, refreshThreshold]);

  useEffect(() => {
    window.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    window.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    window.addEventListener("touchend", handleTouchEnd, {
      passive: true,
    });

    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  });

  return (
    <Box ref={headerRef} className={s.pullHeader}>
      <Box className={s.loaderBox}>
        <CircularLoader
          size={loaderSize}
          color={loaderColor as any}
          sx={{ opacity: 1 }}
          // show progress of reaching treshhold on CircularLoader; start spin when reached
          variant={
            pullState === PullState.ThresholdReached || refreshing
              ? "indeterminate"
              : "determinate"
          }
          value={Math.min((loaderPopupDistance / refreshThreshold) * 100, 100)}
          className={s.pullLoader}
        />
      </Box>
    </Box>
  );
};
